import { z } from 'zod'

import {
  animalStatusSchema,
  breedSchema,
  exitReasonSchema,
  idSchema,
  isoDateSchema,
  labelSchema,
  moneyPaisa,
  noteSchema,
  nonNegativePaisaSchema,
  positivePaisaSchema,
  searchQuerySchema,
  sexSchema,
  sortSchema,
  teethAgeSchema,
} from '../common'
import { cursorQuerySchema } from '../pagination'
import { animalSummarySchema, weightEntrySchema } from './animal'

/* ── A13 · GET /animals ───────────────────────────────────────────────────── */

/**
 * The closed sort enum for the list (§5.3). `-breakEven` and `-costBasis` rank
 * by money and are in `MONEY_SORT_VALUES`: the API rejects them for a Staff
 * account or a locked session, because an ordering by break-even *is* the
 * break-even, in the only form a competitor needs (BR §16 leak 2).
 */
export const ANIMAL_SORTS = [
  'name',
  '-daysHeld',
  'daysHeld',
  'teethIncisors',
  '-breakEven',
  '-costBasis',
] as const

export const animalListQuery = cursorQuerySchema.extend({
  status: animalStatusSchema.optional(),
  lotId: idSchema.optional(),
  /** One search box over nickname, tag and markings — a trigram index, not a DSL. */
  q: searchQuerySchema.optional(),
  sort: sortSchema(ANIMAL_SORTS, '-daysHeld'),
})

/** The page itself. `meta.nextCursor` carries the rest (`pagination.ts`). */
export const animalListResponse = z.array(animalSummarySchema)

/* ── A15 · POST /animals ──────────────────────────────────────────────────── */

/**
 * Add an animal (screen 11).
 *
 * There is no `id`, no `tenantId`, no `createdAt` and **no derived figure** —
 * cost basis, break-even, days held and daily burn are the server's alone.
 * `teethIncisors` is likewise absent: it is derived from `teethAtPurchase`.
 *
 * The four inline amounts at the bottom of the form are not columns on the
 * animal. Each becomes a real `expenses` row in the same transaction, with the
 * right category and `side = PURCHASE`, so a dalali paid at the mandi is the
 * same kind of thing as one entered a week later and both reach cost basis by
 * the same path.
 */
export const createAnimalRequest = z.object({
  tagNumber: z.string().trim().max(40).optional(),
  nickname: z.string().trim().max(80).optional(),
  sex: sexSchema,
  breed: breedSchema.optional(),
  teethAtPurchase: teethAgeSchema,
  colour: z.string().trim().max(40).optional(),
  markings: z.string().trim().max(200).optional(),
  weightInKg: z.number().positive().max(2000).optional(),
  consumptionUnit: z.number().positive().max(9.999).optional(),
  purchaseDate: isoDateSchema,
  purchasePricePaisa: positivePaisaSchema,
  sellerPartyId: idSchema.optional(),
  /** A seller not yet in the book: the server creates the party in the same tx. */
  sellerName: z.string().trim().max(120).optional(),
  purchaseMandi: z.string().trim().max(120).optional(),
  lotId: idSchema.optional(),
  askingPricePaisa: positivePaisaSchema.optional(),
  seasonTag: z.string().trim().max(40).optional(),
  notes: noteSchema.optional(),
  /** Inline purchase-side costs → `expenses` rows (COMMISSION_DALALI, etc.). */
  dalaliPaisa: positivePaisaSchema.optional(),
  transportPaisa: positivePaisaSchema.optional(),
  mandiFeePaisa: positivePaisaSchema.optional(),
  vetPaisa: positivePaisaSchema.optional(),
  /** Media uploaded before the animal existed, adopted on create. */
  mediaIds: z.array(idSchema).max(20).optional(),
})

/* ── A16 · PATCH /animals/:id ─────────────────────────────────────────────── */

/**
 * Edit. Same field set as create minus the inline expense shortcuts — after
 * creation those are expense rows and are edited as expenses (A29), not by
 * rewriting a number on the animal.
 *
 * Sending `purchasePricePaisa` requires the money capability; the guard is
 * server-side, not implied by this schema.
 */
export const patchAnimalRequest = createAnimalRequest
  .omit({
    dalaliPaisa: true,
    transportPaisa: true,
    mandiFeePaisa: true,
    vetPaisa: true,
    mediaIds: true,
  })
  .partial()
  .extend({ status: animalStatusSchema.optional() })
  .refine((v) => Object.keys(v).length > 0, 'Nothing to change.')

/* ── A17/A18 · DELETE /animals/:id · POST /animals/:id/restore ────────────── */

export const softDeleteResponse = z.object({
  id: idSchema,
  deleted: z.literal(true),
  /** 90-day trash (G11). The date the row stops being restorable. */
  purgeOn: isoDateSchema,
})

export const restoreResponse = z.object({ id: idSchema, restored: z.literal(true) })

/* ── A19 · GET /animals/:id/backdate-preview ──────────────────────────────── */

/**
 * "That is 82 days ago — about Rs 43,624 of feed will be added."
 *
 * The Add-animal screen says this **before** saving, so the trader is not
 * surprised by a cost that appears from nowhere. It is a server figure: the
 * client has no feed plan, no consumption unit and no suppression rule, and
 * §4.9 lists feed accrual among the things a client may never compute.
 */
export const backdatePreviewQuery = z.object({ purchaseDate: isoDateSchema })

export const backdatePreviewResponse = z.object({
  daysAgo: z.number().int().nonnegative(),
  estimatedAccrualPaisa: moneyPaisa(),
  /** The rate the estimate used, so the sentence can name it. */
  dailyRatePaisa: moneyPaisa(),
  asOf: isoDateSchema,
})

/* ── A20 · POST /animals/:id/weights · GET ────────────────────────────────── */

/** One weighing. Unique per animal per day, so a correction replaces the day. */
export const createWeightRequest = z.object({
  weighedOn: isoDateSchema,
  weightKg: z.number().positive().max(2000),
  note: noteSchema.optional(),
})

export const weightListResponse = z.array(weightEntrySchema)

/* ── A21 · POST /animals/:id/exit ─────────────────────────────────────────── */

/**
 * Death or loss (§4.11). Always a **realised loss**, always in the period P&L —
 * an animal that died is not "still in stock at cost". `salvageValuePaisa` is
 * whatever the hide or the carcass fetched, and it may legitimately be zero.
 */
export const animalExitRequest = z.object({
  exitDate: isoDateSchema,
  reason: exitReasonSchema,
  deathCause: z.string().trim().max(200).optional(),
  salvageValuePaisa: nonNegativePaisaSchema.optional(),
  note: noteSchema.optional(),
})

export const animalExitResponse = z.object({
  animalId: idSchema,
  exitDate: isoDateSchema,
  reason: exitReasonSchema,
  daysHeld: z.number().int().nonnegative(),
  costBasisPaisa: moneyPaisa(),
  salvageValuePaisa: moneyPaisa(),
  /** `salvage − costBasis`. Negative, and shown as a loss, not a "profit". */
  realisedLossPaisa: moneyPaisa(),
})

/* ── shared ───────────────────────────────────────────────────────────────── */

/** A named animal, for a picker or a link. Money-free by construction. */
export const animalRefSchema = z.object({
  id: idSchema,
  tagNumber: z.string().nullish(),
  nickname: z.string().nullish(),
  label: labelSchema,
})

export type AnimalListQuery = z.input<typeof animalListQuery>
export type CreateAnimalRequest = z.input<typeof createAnimalRequest>
export type PatchAnimalRequest = z.input<typeof patchAnimalRequest>
export type BackdatePreviewQuery = z.input<typeof backdatePreviewQuery>
export type BackdatePreviewResponse = z.infer<typeof backdatePreviewResponse>
export type CreateWeightRequest = z.input<typeof createWeightRequest>
export type AnimalExitRequest = z.input<typeof animalExitRequest>
export type AnimalExitResponse = z.infer<typeof animalExitResponse>
export type SoftDeleteResponse = z.infer<typeof softDeleteResponse>
export type AnimalRef = z.infer<typeof animalRefSchema>
